{
  "$comment": "The seven Structural Duality Principles (SDPs) that form the alphabet of the SDP-adjunction grade monoid G = ⊕_s ℤ/2[Φ(s)]. Each SDP has a unit η (grant / authorize phase) and counit ε (receipt / discharge phase). Canonical authority: theory/manuscripts/contrib/TwoCategoricalCSK/03-SDP-Typing-Kit.md §3.2. Synchronized with AGENTS.md §SDP adjunctions and graded crossing.",
  "sdps": [
    {
      "code": "Tr",
      "name": "TRACE",
      "adjunction": "fibration ⊣ opfibration",
      "neutralizationTheorems": [
        "D1 (DATA.ORIGIN)",
        "D2 (DATA.REACH)"
      ],
      "dualPairGuarantee": "DATA.LINEAGE",
      "icg": "ICG-Tr",
      "color": "#22d3ee",
      "aiScenario": "RAG pipeline lineage",
      "aiSummary": "A retrieval-augmented agent receives a chunk from a vector store; η_Tr is the source-of-record witness, ε_Tr is the discharge on the reading side. Without typed Tr an adversary swaps a chunk's provenance label mid-pipeline (\"lineage substitution\"), turning a low-trust scrape into a vendor-signed claim.",
      "unitDescription": "License-to-cross — the source-of-record witness on a single chunk's lineage.",
      "counitDescription": "License-to-receive — the discharge when the summarizer adopts the chunk.",
      "concreteAttack": "RAG lineage substitution; indirect prompt injection via lineage swap"
    },
    {
      "code": "Al",
      "name": "ALWAYS",
      "adjunction": "F-algebra ⊣ F-coalgebra",
      "neutralizationTheorems": [
        "I1 (INV.ESTABLISH)",
        "I2 (INV.PRESERVE)"
      ],
      "dualPairGuarantee": "INV.CONSISTENCY",
      "icg": "ICG-Al",
      "color": "#22c55e",
      "aiScenario": "multi-model consensus invariant",
      "aiSummary": "A safety classifier (model A) verifies a request and establishes an invariant; a downstream generator (model B) preserves it. η_Al is the establishment phase; ε_Al is the preservation phase. Without typed Al, model B can replay an old establishment certificate and skip actual invariant preservation.",
      "unitDescription": "License-to-cross — F-algebra unit; an invariant has been established.",
      "counitDescription": "License-to-receive — F-coalgebra counit; an invariant has been preserved across a step.",
      "concreteAttack": "multi-model consensus bypass; invariant replay across pipeline stages"
    },
    {
      "code": "St",
      "name": "ACCOUNT",
      "adjunction": "(− ⊗ A) ⊣ (A* ⊗ −)",
      "neutralizationTheorems": [
        "R1 (RES.CONSUME)",
        "R2 (RES.RELEASE)"
      ],
      "dualPairGuarantee": "RES.BALANCE",
      "icg": "ICG-St",
      "color": "#fbbf24",
      "aiScenario": "autonomous-agent quota / tool-call budget",
      "aiSummary": "An autonomous agent has a quota of tool calls per task. η_St is the consume moment when the agent draws against the quota; ε_St is the release moment when the call returned. A typical attack is the double-consume — replaying a single quota grant to authorize two distinct calls.",
      "unitDescription": "License-to-cross — linear-tensor unit; a resource has been consumed.",
      "counitDescription": "License-to-receive — compact-closed counit; a resource has been released.",
      "concreteAttack": "tool-call quota double-spend; release-token replay (use-after-free analogue)"
    },
    {
      "code": "At",
      "name": "REQUIRE",
      "adjunction": "monad ⊣ comonad",
      "neutralizationTheorems": [
        "W (PRECOMMIT)"
      ],
      "dualPairGuarantee": null,
      "icg": "ICG-At",
      "color": "#a78bfa",
      "aiScenario": "LLM-driven CI/CD precommitment",
      "aiSummary": "An LLM is wired into a deploy pipeline; the human operator must precommit before the model emits a deploy command. η_At is the precommit-tracking unit; ε_At is the requirement-discharge counit. The attack is retroactive justification — the model emits the deploy and only then synthesises a fake precommit.",
      "unitDescription": "License-to-cross — commitment-tracking unit; a precommit has been recorded.",
      "counitDescription": "License-to-receive — requirement-discharge counit; the precommit has been honored.",
      "concreteAttack": "autonomous CI/CD credential replay; retroactive precommit fabrication"
    },
    {
      "code": "Dt",
      "name": "DELEGATE",
      "adjunction": "slice ⊣ coslice",
      "neutralizationTheorems": [
        "P (PERMIT)"
      ],
      "dualPairGuarantee": null,
      "icg": "ICG-Dt",
      "color": "#ec4899",
      "aiScenario": "multi-agent capability handoff",
      "aiSummary": "A Planner agent delegates compile to a Coder agent. η_Dt is the grant; ε_Dt is the Coder's receipt. The attack is scope drift — Coder reuses the compile-scope grant to authorize a deploy.",
      "unitDescription": "License-to-cross — authority delegation unit; a capability has been granted.",
      "counitDescription": "License-to-receive — delegation-receipt counit; the grant has been acknowledged.",
      "concreteAttack": "multi-agent delegation misuse; MCP cross-server capability replay"
    },
    {
      "code": "De",
      "name": "DECOMPOSE",
      "adjunction": "operad ⊣ cooperad",
      "neutralizationTheorems": [
        "C (COMPOSE)"
      ],
      "dualPairGuarantee": null,
      "icg": "ICG-De",
      "color": "#3b82f6",
      "aiScenario": "LLM-generated plan tree",
      "aiSummary": "A planning LLM decomposes a task into subtasks. η_De is the operadic composition unit that authorizes the decomposition; ε_De is the cooperadic counit that authorizes a re-bracketing. The attack is bracket laundering — re-parenting a subtask under a high-trust node to inherit its privileges.",
      "unitDescription": "License-to-cross — operadic composition unit; a subtask tree has been composed.",
      "counitDescription": "License-to-receive — cooperadic counit; a decomposition / re-bracketing has been authorized.",
      "concreteAttack": "decompose-and-recompose jailbreak; bracket laundering across plan trees"
    },
    {
      "code": "Pa",
      "name": "PARTITION",
      "adjunction": "parameterized self-dual",
      "neutralizationTheorems": [
        "S (SEPARATE)"
      ],
      "dualPairGuarantee": null,
      "icg": "ICG-Pa",
      "color": "#14b8a6",
      "aiScenario": "tenant sandbox / cross-server boundary",
      "aiSummary": "Two tools, tenants, or execution sandboxes share a runtime. η_Pa is the split (boundary establishment); ε_Pa is the recombination (controlled merge). A typical attack is unauthorized merge — side-channeling data from sandbox A into sandbox B without a fresh recombination authorization.",
      "unitDescription": "License-to-cross — partition-splitting unit; a boundary has been established.",
      "counitDescription": "License-to-receive — partition-recombination counit; two regions have been recombined.",
      "concreteAttack": "MCP cross-server confused deputy; cross-tenant prompt-injection chain"
    }
  ]
}
